import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, Button, FormControl, FormLabel, ListItem, IconButton, Link, Typography, Autocomplete } from '@mui/material';
import { useIntl } from 'react-intl';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useQuoteMutations } from '../hooks/useQuoteMutations';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { useQuoteAttachmentDropzone } from '../hooks/useQuoteAttachmentDropzone';
import { UploadZoneRoot } from '@components/FileUpload/UploadZoneRoot';
import { UploadFile, Delete } from '@mui/icons-material';
import { DocumentFileNameAndIcon } from '@modules/documents/components/DocumentFilenameAndIcon';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { useBudgetItemsQuery, useDeleteQuoteAttachmentMutation, useGetProjectBudgetQuery } from 'gql/index';
type Props = {
  open: boolean;
  onClose: () => void;
  costItemId?: number;
  entryTypeSwitcher?: ReactNode;
  quote?: {
    id: number;
    name: string;
    amount: number;
    quoteDate: string;
    attachments?: { id: number; fileName: string; thumbnailStatus?: string }[];
  };
};

type FormValues = {
  name: string;
  amount: number;
  quoteDate: string;
};

export const QuoteDialog: React.FC<Props> = ({ open, onClose, costItemId: costItemIdProp, entryTypeSwitcher, quote }) => {
  const { formatMessage } = useIntl();
  const { addQuote, editQuote } = useQuoteMutations();
  const deleteQuoteAttachment = useDeleteQuoteAttachmentMutation();
  const invalidateQuery = useQueryInvalidator();
  const { projectId } = useCurrentProject();
  const { data: budgetItems } = useBudgetItemsQuery({ projectId: projectId ?? 0 }, { select: d => d.budgetItems, enabled: open && !quote && costItemIdProp == null });
  const [currentAttachment, setCurrentAttachment] = useState<{ id?: number; fileName: string } | null>(null);
  const [fileSender, setFileSender] = useState<((quoteId: number, onComplete: () => void) => void) | null>(null);
  const [selectedCostItemId, setSelectedCostItemId] = useState<number | null>(null);

  const costItemId = quote ? costItemIdProp : (costItemIdProp ?? selectedCostItemId);

  const costItemOptions = useMemo(() => {
    const items = budgetItems ?? [];
    const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name));
    return sorted.flatMap(budgetItem => {
      const costItems = [...(budgetItem.costItems ?? [])].sort((a, b) => a.name.localeCompare(b.name));
      return costItems.map(costItem => ({
        id: costItem.id,
        name: costItem.name,
        group: budgetItem.name,
      }));
    });
  }, [budgetItems]);

  const selectedCostItemOption = useMemo(
    () => costItemOptions.find(o => o.id === selectedCostItemId) ?? null,
    [costItemOptions, selectedCostItemId],
  );
  const { getInputProps, getRootProps, isDragActive } = useQuoteAttachmentDropzone({
    disabled: addQuote.isPending || editQuote.isPending,
    onFileUploadReady: (sender, filename) => {
      setCurrentAttachment({ fileName: filename });
      setFileSender(() => sender);
    }
  });

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      name: '',
      amount: 0,
      quoteDate: dayjs().format('YYYY-MM-DD'),
    },
  });

  useEffect(() => {
    if (!open) {
      setSelectedCostItemId(null);
      return;
    }
    reset({
      name: quote?.name ?? '',
      amount: quote?.amount ?? 0,
      quoteDate: quote?.quoteDate ? dayjs(quote.quoteDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    });
    if (quote?.attachments?.length) {
      const attachment = quote.attachments[0];
      setCurrentAttachment({ id: attachment.id, fileName: attachment.fileName });
    } else {
      setCurrentAttachment(null);
    }
    setFileSender(null);
  }, [quote, reset, open]);

  const onSubmit = handleSubmit(values => {
    const effectiveCostItemId = costItemId ?? undefined;
    if (effectiveCostItemId == null) return;
    const finalize = () => {
      setCurrentAttachment(null);
      setFileSender(null);
      onClose();
      reset();
    };
    if (quote) {
      editQuote.mutate({
        input: {
          id: quote.id,
          projectId: projectId ?? 0,
          costItemId: effectiveCostItemId,
          name: values.name,
          amount: Number(values.amount),
          quoteDate: dayjs(values.quoteDate).toISOString(),
        },
      }, {
        onSuccess: async () => {
          const didUserRemoveAttachment = !currentAttachment && (quote.attachments?.length ?? 0) > 0;
          if (didUserRemoveAttachment && projectId != null) {
            await deleteQuoteAttachment.mutateAsync({
              input: { projectId, quoteId: quote.id },
            });
            invalidateQuery(useBudgetItemsQuery, { projectId });
            invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
          }
          else {
            invalidateQuery(useBudgetItemsQuery, { projectId });
            invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
          }
          
          if (fileSender) {
            fileSender(quote.id, () => {
              if (projectId != null) {
                invalidateQuery(useBudgetItemsQuery, { projectId });
                invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
              }
              finalize();
            });
          } else {
            finalize();
          }
        }
      });
      return;
    }

    addQuote.mutate({
      input: {
        projectId: projectId ?? 0,
        costItemId: effectiveCostItemId,
        name: values.name,
        amount: Number(values.amount),
        quoteDate: dayjs(values.quoteDate).toISOString(),
      },
    }, {
      onSuccess: (result: any) => {
        const newId = result?.addQuote?.quote?.id;
        if (newId && fileSender) {
          fileSender(newId, () => {
            if (projectId != null) {
              invalidateQuery(useBudgetItemsQuery, { projectId });
              invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
            }
            finalize();
          });
        } else {
          finalize();
        }
      }
    });
  });

  const saving = addQuote.isPending || editQuote.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        <Stack gap={1}>
          <Typography variant='h6'>
            {quote ? formatMessage({ id: 'Edit quote' }) : formatMessage({ id: 'Add quote' })}
          </Typography>
          {entryTypeSwitcher}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {!quote && costItemIdProp == null && (
            <FormControl fullWidth required>
              <Autocomplete<{ id: number; name: string; group: string }, false, false>
                options={costItemOptions}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedCostItemOption}
                onChange={(_, option) => setSelectedCostItemId(option?.id ?? null)}
                renderInput={(params) => (
                  <TextField {...params} label={formatMessage({ id: 'Cost item' })} required />
                )}
              />
            </FormControl>
          )}
          <TextField
            required
            label={formatMessage({ id: 'Quote name' })}
            {...register('name', { required: true })}
          />
          <TextField
            required
            type='number'
            label={formatMessage({ id: 'Amount' })}
            inputProps={{ min: 0, step: 0.01 }}
            {...register('amount', { required: true, valueAsNumber: true })}
          />
          <TextField
            required
            type='date'
            label={formatMessage({ id: 'Quote date' })}
            InputLabelProps={{ shrink: true }}
            {...register('quoteDate', { required: true })}
          />
          <Stack>
            <FormLabel>{formatMessage({ id: 'Attachment' })}</FormLabel>
            {currentAttachment ? (
              <ListItem disablePadding sx={{ border: t => `1px solid ${t.palette.divider}` }}>
                <DocumentFileNameAndIcon
                  fileName={currentAttachment.fileName}
                  downloadLink={currentAttachment.id ? `/${getTenantIdentifier()}/api/Documents/${currentAttachment.id}` : undefined}
                  actionButton={
                    <IconButton
                      disabled={addQuote.isPending || editQuote.isPending}
                      onClick={() => {
                        setCurrentAttachment(null);
                        setFileSender(null);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  }
                />
              </ListItem>
            ) : (
              <UploadZoneRoot isDragActive={isDragActive} dropZoneRootProps={getRootProps()}>
                <UploadFile color='primary' />
                <input {...getInputProps()} />
                <Stack alignItems='center' gap={1}>
                  <Typography variant='body1'>
                    <Link href='#'>
                      {formatMessage({ id: 'Click to upload' })}
                    </Link>
                    {' '}{formatMessage({ id: 'or drag and drop' })}
                  </Typography>
                  <Typography variant='body1' color='grey.600'>{formatMessage({ id: '(10MB max)' })}</Typography>
                </Stack>
              </UploadZoneRoot>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button variant='contained' onClick={onSubmit} disabled={saving || (!!open && !quote && costItemIdProp == null && selectedCostItemId == null)}>
          {formatMessage({ id: 'Save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
